import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Person,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';  

const HeaderFixed = styled(Box)(({ scrolled }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  zIndex: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 5%',
  pointerEvents: 'none',
  flexWrap: 'nowrap',
  '@media (max-width: 800px)': {
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: '0rem 5%',
  },
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  pointerEvents: 'auto',
  background: 'transparent',
  padding: 0,
  margin: 0,
  position: 'relative',
  left: '20px',
  '@media (max-width: 800px)': {
    left: 0,
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
});

const LogoImg = styled('img')({
  height: '182px',
  width: 'auto',
  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
  transition: 'all 0.3s ease',
  display: 'block',
  '@media (max-width: 800px)': {
    height: '127px',
  },
});

const LogoTextWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '182px',
  lineHeight: 1,
  marginLeft: '0.5rem',
  textAlign: 'center',
  '@media (max-width: 800px)': {
    height: '127px',
  },
});

const LogoTextPart1 = styled(Typography)({
  fontFamily: '"Orbitron", sans-serif',
  fontWeight: 400,
  fontSize: '2.2rem',
  color: 'white',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  whiteSpace: 'nowrap',
  lineHeight: 0.8,
  marginBottom: '-0.3rem',
  display: 'block',
  background: 'transparent',
  '@media (max-width: 800px)': {
    fontSize: '2.4rem',
  },
});

const LogoTextPart2 = styled(Typography)({
  fontFamily: '"Helvetica Neue", "Helvetica", "Arial Black", sans-serif',
  fontWeight: 900,
  fontSize: '3.6rem',
  color: '#ffb45a',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  whiteSpace: 'nowrap',
  lineHeight: 0.8,
  marginTop: '-0.3rem',
  display: 'block',
  background: 'transparent',
  '@media (max-width: 800px)': {
    fontSize: '2.4rem',
  },
});

const Navbar = styled(Box)(({ scrolled }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  padding: '0.6rem 1.5rem',
  backgroundColor: scrolled ? 'rgba(0, 0, 0, 0.35)' : 'transparent',
  backdropFilter: scrolled ? 'blur(4px)' : 'none',
  boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.1)' : 'none',
  borderRadius: '60px',
  transition: 'background-color 0.6s ease, backdrop-filter 0.6s ease, box-shadow 0.6s ease',
  pointerEvents: 'auto',
  flexWrap: 'wrap',
  gap: '1.5rem',
  zIndex: 101,
  flexShrink: 0,
  '@media (max-width: 800px)': {
    padding: '0.5rem 1rem',
    marginTop: '0.5rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
}));

const NavLinks = styled(Box)({
  display: 'flex',
  gap: '2rem',
  alignItems: 'center',
  flexWrap: 'wrap',
  '@media (max-width: 800px)': {
    gap: '1rem',
  },
});

// CORREGIDO: Ahora es un span con estilos de link simple, sin padding ni márgenes
const NavLink = styled('span')({
  color: 'white',
  fontWeight: 500,
  fontSize: '1rem',
  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline',
  background: 'transparent',
  padding: 0,
  margin: 0,
  '&:hover': {
    color: '#ffb45a',
  },
});

const LoginBtn = styled(Button)({
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(5px)',
  border: '2px solid rgba(255, 255, 255, 0.6)',
  color: 'white',
  padding: '0.5rem 1.8rem',
  borderRadius: '50px',
  fontWeight: 600,
  fontSize: '0.95rem',
  gap: '0.4rem',
  whiteSpace: 'nowrap',
  minWidth: 'auto',
  lineHeight: 1,
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderColor: 'white',
  },
  '& i': {
    fontSize: '0.9rem',
  },
});

const SocialNav = styled(Box)({
  display: 'flex',
  gap: '0.8rem',
  alignItems: 'center',
  marginLeft: '0.5rem',
  '@media (max-width: 800px)': {
    marginLeft: 0,
    marginTop: '0.5rem',
  },
});

const SocialLink = styled('a')({
  color: 'white',
  fontSize: '1.2rem',
  transition: 'all 0.3s ease',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255,255,255,0.3)',
  '&:hover': {
    color: '#ffb45a',
    transform: 'translateY(-3px)',
    background: 'rgba(255,255,255,0.2)',
    borderColor: '#ffb45a',
  },
});

const Dropdown = styled(Box)({
  position: 'relative',
  display: 'inline-block',
});

// CORREGIDO: Dropbtn también como span simple
const Dropbtn = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  color: 'white',
  fontWeight: 500,
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  fontSize: '1rem',
  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
  '&:hover': {
    color: '#ffb45a',
  },
});

const DropdownContent = styled(Box)({
  position: 'absolute',
  top: '100%',
  left: 0,
  background: 'white',
  minWidth: '220px',
  boxShadow: '0 20px 40px rgba(0, 20, 40, 0.25)',
  borderRadius: '16px',
  padding: '0.6rem 0',
  zIndex: 10000,
  marginTop: '0.8rem',
  border: '1px solid rgba(233, 124, 7, 0.2)',
  animation: 'slideDown 0.25s ease',
  '@keyframes slideDown': {
    from: {
      opacity: 0,
      transform: 'translateY(-15px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const DropdownItem = styled('a')({
  color: '#0b2b44',
  padding: '0.9rem 1.5rem',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontWeight: 500,
  cursor: 'pointer',
  borderLeft: '4px solid transparent',
  '&:hover': {
    background: '#f0f7ff',
    borderLeft: '4px solid #e97c07',
    paddingLeft: '1.8rem',
  },
  '& i': {
    color: '#e97c07',
    width: '22px',
    fontSize: '1.2rem',
  },
});

export default function Header({ onLoginClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [showSolutionsMenu, setShowSolutionsMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const dropdown = document.querySelector('.dropdown');
      if (dropdown && !dropdown.contains(e.target)) {
        setShowSolutionsMenu(false);
      }
    };
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleSolutionsMenu = (e) => {
    e.stopPropagation();
    setShowSolutionsMenu(!showSolutionsMenu);
  };

  const closeSolutionsMenu = () => {
    setShowSolutionsMenu(false);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

//   const scrollToSection = (id) => {
//     const element = document.getElementById(id);
//     if (element) {
//       element.scrollIntoView({ behavior: 'smooth' });
//     }
//     handleMobileMenuClose();
//   };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100; 
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth'
            });
        }
        handleMobileMenuClose();
    };

    // const scrollToSection = (id) => {
    //     const element = document.getElementById(id);
    //     if (element) {
    //         element.scrollIntoView({ behavior: 'smooth' });
    //     }
    //     handleMobileMenuClose();
    // };
    const handleLogout = async () => {
      await signOut(auth);
    };
  return (
    <HeaderFixed scrolled={scrolled ? 1 : 0}>
        <LogoContainer>
            <LogoImg src="/images/logo1A.png" alt="Mi Gran Tienda" />
            <LogoTextWrapper>
            <LogoTextPart1>Mi</LogoTextPart1>
            <LogoTextPart1>Gran</LogoTextPart1>
            <LogoTextPart2>Tienda</LogoTextPart2>
            </LogoTextWrapper>
        </LogoContainer>

        <Navbar scrolled={scrolled ? 1 : 0}>
            {/* CONTENEDOR FLEX PARA ALINEAR TODO */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                
                {/* MENÚ DE ESCRITORIO - visible solo en md y mayores */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '1.5rem' }}>
                <NavLinks>
                    <NavLink onClick={() => scrollToSection('inicio')}>Inicio</NavLink>
                    
                    <Dropdown className="dropdown">
                    <Dropbtn onClick={toggleSolutionsMenu}>
                        Soluciones {showSolutionsMenu ? <KeyboardArrowUp sx={{ fontSize: 16 }} /> : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
                    </Dropbtn>
                    {showSolutionsMenu && (
                        <DropdownContent>
                        {/* Acanto 1 removido intencionalmente — acceso bloqueado para todos los usuarios */}
                        <DropdownItem onClick={closeSolutionsMenu}>
                            <i className="fas fa-city"></i> Próximamente
                        </DropdownItem>
                        </DropdownContent>
                    )}
                    </Dropdown>

                    <NavLink onClick={() => scrollToSection('pilares')}>Servicios</NavLink>
                    <NavLink onClick={() => scrollToSection('club')}>Club</NavLink>
                    <NavLink onClick={() => scrollToSection('emprende')}>Emprende</NavLink>
                    <NavLink onClick={() => scrollToSection('contacto')}>Contacto</NavLink>

                    {/* <LoginBtn startIcon={<Person />}>
                    Login
                    </LoginBtn> */}
                    {user ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {user.email}
                        </Typography>
                        <Button 
                          size="small"
                          onClick={handleLogout}
                          sx={{ color: 'white', borderColor: 'white' }}
                        >
                          Salir
                        </Button>
                      </Box>
                    ) : (
                      <LoginBtn startIcon={<Person />} onClick={onLoginClick}>
                        Login
                      </LoginBtn>
                    )}
                </NavLinks>

                <SocialNav>
                    <SocialLink href="#" target="_blank" aria-label="Facebook">
                    <i className="fab fa-facebook-f"></i>
                    </SocialLink>
                    <SocialLink href="#" target="_blank" aria-label="Instagram">
                    <i className="fab fa-instagram"></i>
                    </SocialLink>
                    <SocialLink href="#" target="_blank" aria-label="Twitter">
                    <i className="fab fa-twitter"></i>
                    </SocialLink>
                    <SocialLink href="#" target="_blank" aria-label="LinkedIn">
                    <i className="fab fa-linkedin-in"></i>
                    </SocialLink>
                </SocialNav>
                </Box>

                {/* MENÚ MÓVIL - solo el icono de hamburguesa */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                <IconButton onClick={handleMobileMenuOpen} sx={{ color: 'white' }}>
                    <MenuIcon />
                </IconButton>
                </Box>
            </Box>
        </Navbar>

        {/* DRAWER MÓVIL - menú lateral completo */}
        <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={handleMobileMenuClose}
            PaperProps={{
                sx: {
                    width: '280px',
                    backgroundColor: '#0b2b44',
                    color: 'white',
                },
            }}
        >
            <Box sx={{ width: '100%', height: '100%' }}>
                {/* Header del drawer */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffb45a' }}>
                        Menú
                    </Typography>
                    <IconButton onClick={handleMobileMenuClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Lista de opciones */}
                <List sx={{ padding: 0 }}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => scrollToSection('inicio')}
                            sx={{
                                padding: '1rem 1.5rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 180, 90, 0.1)',
                                    borderLeft: '4px solid #ffb45a',
                                },
                            }}
                        >
                            <ListItemText
                                primary="Inicio"
                                primaryTypographyProps={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={handleMobileMenuClose}
                            sx={{
                                padding: '1rem 1.5rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 180, 90, 0.1)',
                                    borderLeft: '4px solid #ffb45a',
                                },
                            }}
                        >
                            <ListItemText
                                primary="Soluciones (Próximamente)"
                                primaryTypographyProps={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => scrollToSection('pilares')}
                            sx={{
                                padding: '1rem 1.5rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 180, 90, 0.1)',
                                    borderLeft: '4px solid #ffb45a',
                                },
                            }}
                        >
                            <ListItemText
                                primary="Servicios"
                                primaryTypographyProps={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => scrollToSection('club')}
                            sx={{
                                padding: '1rem 1.5rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 180, 90, 0.1)',
                                    borderLeft: '4px solid #ffb45a',
                                },
                            }}
                        >
                            <ListItemText
                                primary="Club"
                                primaryTypographyProps={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => scrollToSection('emprende')}
                            sx={{
                                padding: '1rem 1.5rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 180, 90, 0.1)',
                                    borderLeft: '4px solid #ffb45a',
                                },
                            }}
                        >
                            <ListItemText
                                primary="Emprende"
                                primaryTypographyProps={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => scrollToSection('contacto')}
                            sx={{
                                padding: '1rem 1.5rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 180, 90, 0.1)',
                                    borderLeft: '4px solid #ffb45a',
                                },
                            }}
                        >
                            <ListItemText
                                primary="Contacto"
                                primaryTypographyProps={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', margin: '0.5rem 0' }} />

                    <ListItem disablePadding>
                        <ListItemButton
                            // onClick={handleMobileMenuClose}
                            onClick={() => {
                                if (user) {
                                    handleLogout();
                                } else {
                                    onLoginClick();
                                }
                                handleMobileMenuClose();
                            }}
                            sx={{
                                padding: '1rem 1.5rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 180, 90, 0.1)',
                                    borderLeft: '4px solid #ffb45a',
                                },
                            }}
                        >
                            <Person sx={{ marginRight: '0.5rem', color: '#ffb45a' }} />
                            <ListItemText
                                // primary="Login"
                                primary={user ? 'Cerrar sesión' : 'Login'}
                                primaryTypographyProps={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>

                {/* Redes sociales en el footer del drawer */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    padding: '1.5rem',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                }}>
                    <Typography variant="body2" sx={{ marginBottom: '1rem', color: 'rgba(255,255,255,0.7)' }}>
                        Síguenos
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'center',
                    }}>
                        <SocialLink href="#" target="_blank" aria-label="Facebook">
                            <i className="fab fa-facebook-f"></i>
                        </SocialLink>
                        <SocialLink href="#" target="_blank" aria-label="Instagram">
                            <i className="fab fa-instagram"></i>
                        </SocialLink>
                        <SocialLink href="#" target="_blank" aria-label="Twitter">
                            <i className="fab fa-twitter"></i>
                        </SocialLink>
                        <SocialLink href="#" target="_blank" aria-label="LinkedIn">
                            <i className="fab fa-linkedin-in"></i>
                        </SocialLink>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    </HeaderFixed>
  );
}